import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { EvaluationPeriodConfig } from '../../types';
import {
  getEvaluationPeriods,
  createEvaluationPeriod,
  updateEvaluationPeriod,
  deleteEvaluationPeriod,
} from '../../lib/db/index';

export function EvaluationPeriodManager() {
  const [periods, setPeriods] = useState<EvaluationPeriodConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editKey, setEditKey] = useState('');
  const [editLabel, setEditLabel] = useState('');
  const [newKey, setNewKey] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);

  const loadPeriods = async () => {
    try {
      setLoading(true);
      const data = await getEvaluationPeriods();
      setPeriods(data);
    } catch (err) {
      console.error(err);
      setError('Error al cargar los periodos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPeriods(); }, []);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKey.trim() || !newLabel.trim()) return;
    if (periods.some(p => p.key === newKey.trim())) {
      setError('Ya existe un periodo con esa clave');
      return;
    }
    try {
      setError(null);
      const nextOrder = periods.length > 0 ? Math.max(...periods.map(p => p.sortOrder)) + 1 : 1;
      const created = await createEvaluationPeriod(newKey.trim(), newLabel.trim(), nextOrder);
      setPeriods([...periods, created]);
      setNewKey('');
      setNewLabel('');
      setAdding(false);
    } catch (err) {
      console.error(err);
      setError('Error al crear el periodo');
    }
  };

  const handleEdit = (period: EvaluationPeriodConfig) => {
    setEditingId(period.id);
    setEditKey(period.key);
    setEditLabel(period.label);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editKey.trim() || !editLabel.trim()) return;
    const current = periods.find(p => p.id === editingId);
    if (!current) return;
    // Check key uniqueness (excluding current)
    if (periods.some(p => p.id !== editingId && p.key === editKey.trim())) {
      setError('Ya existe un periodo con esa clave');
      return;
    }
    try {
      setError(null);
      await updateEvaluationPeriod(editingId, editKey.trim(), editLabel.trim(), current.sortOrder);
      setPeriods(periods.map(p =>
        p.id === editingId ? { ...p, key: editKey.trim(), label: editLabel.trim() } : p
      ));
      setEditingId(null);
    } catch (err) {
      console.error(err);
      setError('Error al actualizar el periodo');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar este periodo de evaluación? Las notas asociadas no se borrarán pero no serán accesibles desde este periodo.')) return;
    try {
      setError(null);
      await deleteEvaluationPeriod(id);
      setPeriods(periods.filter(p => p.id !== id));
    } catch (err) {
      console.error(err);
      setError('Error al eliminar el periodo');
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const newPeriods = [...periods];
    const prev = newPeriods[index - 1];
    const curr = newPeriods[index];
    // Swap sort orders
    try {
      await updateEvaluationPeriod(curr.id, curr.key, curr.label, prev.sortOrder);
      await updateEvaluationPeriod(prev.id, prev.key, prev.label, curr.sortOrder);
      const tmpOrder = curr.sortOrder;
      curr.sortOrder = prev.sortOrder;
      prev.sortOrder = tmpOrder;
      newPeriods[index - 1] = curr;
      newPeriods[index] = prev;
      setPeriods(newPeriods);
    } catch (err) {
      console.error(err);
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === periods.length - 1) return;
    await handleMoveUp(index + 1);
  };

  if (loading) return <div className="py-8 text-center text-slate-400">Cargando...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Periodos de Evaluación</h2>
        {!adding && (
          <button
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4" />
            Añadir Periodo
          </button>
        )}
      </div>

      {error && (
        <div className="p-3 mb-4 text-sm text-red-700 bg-red-50 rounded-lg">{error}</div>
      )}

      <p className="mb-4 text-sm text-slate-500">
        Configura los periodos de evaluación disponibles para este centro. La <strong>clave</strong> es el identificador interno (ej: "1", "2", "final") y la <strong>etiqueta</strong> es lo que se muestra en la interfaz.
      </p>

      {/* Add form */}
      {adding && (
        <form onSubmit={handleAdd} className="p-4 mb-4 rounded-lg border border-indigo-200 bg-indigo-50">
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="block mb-1 text-xs font-medium text-slate-600">Clave</label>
              <input
                type="text"
                placeholder="ej: 1, 2, final"
                value={newKey}
                onChange={e => setNewKey(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                autoFocus
              />
            </div>
            <div className="flex-[2]">
              <label className="block mb-1 text-xs font-medium text-slate-600">Etiqueta</label>
              <input
                type="text"
                placeholder="ej: 1ª Evaluación"
                value={newLabel}
                onChange={e => setNewLabel(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700">
              Crear
            </button>
            <button type="button" onClick={() => { setAdding(false); setNewKey(''); setNewLabel(''); }} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white rounded-md border border-slate-300 hover:bg-slate-50">
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* List */}
      <div className="space-y-2">
        {periods.length === 0 && (
          <p className="py-8 text-center text-slate-400">No hay periodos configurados. Añade uno para empezar.</p>
        )}
        {periods.map((period, index) => (
          <div
            key={period.id}
            className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300"
          >
            {/* Reorder buttons */}
            <div className="flex flex-col gap-0.5">
              <button
                onClick={() => handleMoveUp(index)}
                disabled={index === 0}
                className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                title="Subir"
              >
                <svg className="w-3 h-3" viewBox="0 0 12 12"><path d="M6 3L2 7h8z" fill="currentColor"/></svg>
              </button>
              <button
                onClick={() => handleMoveDown(index)}
                disabled={index === periods.length - 1}
                className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                title="Bajar"
              >
                <svg className="w-3 h-3" viewBox="0 0 12 12"><path d="M6 9L2 5h8z" fill="currentColor"/></svg>
              </button>
            </div>

            {editingId === period.id ? (
              <div className="flex flex-1 gap-3 items-center">
                <div className="flex-1">
                  <input
                    type="text"
                    value={editKey}
                    onChange={e => setEditKey(e.target.value)}
                    className="w-full px-2 py-1 text-sm rounded border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Clave"
                  />
                </div>
                <div className="flex-[2]">
                  <input
                    type="text"
                    value={editLabel}
                    onChange={e => setEditLabel(e.target.value)}
                    className="w-full px-2 py-1 text-sm rounded border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Etiqueta"
                    autoFocus
                  />
                </div>
                <button onClick={handleSaveEdit} className="px-3 py-1 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700">
                  Guardar
                </button>
                <button onClick={() => setEditingId(null)} className="px-3 py-1 text-xs font-medium text-slate-600 bg-slate-100 rounded hover:bg-slate-200">
                  Cancelar
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 min-w-0">
                  <span className="font-semibold text-slate-700">{period.label}</span>
                  <span className="ml-2 text-xs text-slate-400">clave: {period.key}</span>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button
                    onClick={() => handleEdit(period)}
                    className="p-1.5 text-slate-400 hover:text-indigo-600 rounded hover:bg-indigo-50"
                    title="Editar"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(period.id)}
                    className="p-1.5 text-slate-400 hover:text-red-600 rounded hover:bg-red-50"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
